import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Box,
  Stack,
  TextField,
  Slide,
  Grow,
  Zoom,
  Fade,
  Paper,
  Avatar,
  Divider,
} from "@mui/material";
import { keyframes } from "@mui/system";

import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentIcon from "@mui/icons-material/Payment";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx"; // Add this import

// Premium Animations
const fadeInUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const slideInLeft = keyframes`
  from { transform: translateX(-50px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

const rotateIn = keyframes`
  from { transform: rotate(-180deg) scale(0); opacity: 0; }
  to { transform: rotate(0deg) scale(1); opacity: 1; }
`;

// Transition component
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function PaymentList() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Filter states
  const [filterUser, setFilterUser] = useState("");
  const [filterPlan, setFilterPlan] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);

  const token = localStorage.getItem("token");
  const metaRed = "#C62828";
  const metaGreen = "#2E7D32";
  const metaBlue = "#1565C0";
  const metaOrange = "#ED6C02";

  // Fetch all payments
  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/payment/get-payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setPayments(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch payments", "error");
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
      showSnackbar(error.response?.data?.message || "Error fetching payments", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle view dialog open
  const handleViewDialog = (payment) => {
    setSelectedPayment(payment);
    setViewDialogOpen(true);
  };

  // Clear filters
  const clearFilters = () => {
    setFilterUser("");
    setFilterPlan("");
    setFilterStatus("");
  };

  const exportToExcel = () => {
    if (filteredPayments.length === 0) {
      showSnackbar("No data to export", "warning");
      return;
    }

    setExporting(true);
    try {
      // Prepare data for Excel
      const exportData = filteredPayments.map((payment, index) => ({
        "S.No": index + 1,
        "User Name": payment.user?.name || "N/A",
        "User Email": payment.user?.email_id || "N/A",
        "Plan Name": payment.plan?.name || "N/A",
        "Amount (₹)": payment.amount,
        "Order ID": payment.razorpay_order_id,
        Status: payment.status?.toUpperCase(),
        Date: formatDate(payment.createdAt),
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns (optional)
      const colWidths = [
        { wch: 6 }, // S.No
        { wch: 20 }, // User Name
        { wch: 25 }, // User Email
        { wch: 15 }, // Plan Name
        { wch: 12 }, // Amount
        { wch: 25 }, // Order ID
        { wch: 12 }, // Status
        { wch: 20 }, // Date
      ];
      ws["!cols"] = colWidths;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Payments");

      // Generate Excel file and trigger download
      XLSX.writeFile(wb, `payments_${new Date().toISOString().slice(0, 19)}.xlsx`);

      showSnackbar("Export completed successfully!", "success");
    } catch (error) {
      console.error("Export error:", error);
      showSnackbar("Failed to export data", "error");
    } finally {
      setExporting(false);
    }
  };

  // Get unique users and plans for filters
  const uniqueUsers = useMemo(() => {
    const users = new Set();
    payments.forEach((payment) => {
      if (payment.user?.name) {
        users.add(payment.user.name);
      }
    });
    return Array.from(users);
  }, [payments]);

  const uniquePlans = useMemo(() => {
    const plans = new Set();
    payments.forEach((payment) => {
      if (payment.plan?.name) {
        plans.add(payment.plan.name);
      }
    });
    return Array.from(plans);
  }, [payments]);

  // Filtered payments
  const filteredPayments = useMemo(() => {
    let filtered = [...payments];

    if (filterUser) {
      filtered = filtered.filter((item) =>
        item.user?.name?.toLowerCase().includes(filterUser.toLowerCase())
      );
    }

    if (filterPlan) {
      filtered = filtered.filter((item) =>
        item.plan?.name?.toLowerCase().includes(filterPlan.toLowerCase())
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((item) => item.status === filterStatus);
    }

    return filtered;
  }, [payments, filterUser, filterPlan, filterStatus]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format amount
  const formatAmount = (amount) => {
    return `₹${amount}`;
  };

  // Get status chip
  const getStatusChip = (status) => {
    switch (status) {
      case "success":
      case "captured":
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="SUCCESS"
            size="small"
            sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: "bold" }}
          />
        );
      case "created":
      case "pending":
        return (
          <Chip
            icon={<PendingIcon />}
            label="PENDING"
            size="small"
            sx={{ bgcolor: "#fff3e0", color: "#ed6c02", fontWeight: "bold" }}
          />
        );
      case "failed":
      case "cancelled":
        return (
          <Chip
            icon={<CancelIcon />}
            label="FAILED"
            size="small"
            sx={{ bgcolor: "#ffebee", color: "#d32f2f", fontWeight: "bold" }}
          />
        );
      default:
        return <Chip label={status?.toUpperCase()} size="small" />;
    }
  };

  const columns = [
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          USER
        </MDTypography>
      ),
      accessor: "user",
      align: "left",
      //   width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          PLAN
        </MDTypography>
      ),
      accessor: "plan",
      align: "left",
      //   width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          AMOUNT
        </MDTypography>
      ),
      accessor: "amount",
      align: "center",
      //   width: "10%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          ORDER ID
        </MDTypography>
      ),
      accessor: "order_id",
      align: "left",
      //   width: "20%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          PAYMENT ID
        </MDTypography>
      ),
      accessor: "payment_id",
      align: "left",
      //   width: "20%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          STATUS
        </MDTypography>
      ),
      accessor: "status",
      align: "center",
      //   width: "10%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          DATE
        </MDTypography>
      ),
      accessor: "createdAt",
      align: "center",
      //   width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          ACTIONS
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
      //   width: "10%",
    },
  ];

  const rows = useMemo(
    () =>
      filteredPayments.map((payment, index) => ({
        user: (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Avatar
              src={payment.user?.profile_photo}
              sx={{ width: 32, height: 32, border: `1px solid ${metaBlue}` }}
            >
              <PersonIcon />
            </Avatar>
            <MDBox>
              <MDTypography variant="button" fontWeight="bold" fontSize="0.75rem">
                {payment.user?.name || "N/A"}
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="medium"
                display="block"
                fontSize="0.65rem"
              >
                {payment.user?.email_id}
              </MDTypography>
            </MDBox>
          </MDBox>
        ),
        plan: (
          <MDTypography variant="button" fontWeight="medium" fontSize="0.75rem">
            {payment.plan?.name || "N/A"}
          </MDTypography>
        ),
        amount: (
          <MDTypography variant="h6" fontWeight="bold" color="error">
            {formatAmount(payment.amount)}
          </MDTypography>
        ),
        order_id: (
          <MDTypography variant="caption" fontWeight="medium" fontSize="0.7rem">
            {payment.razorpay_order_id}
          </MDTypography>
        ),
        payment_id: (
          <MDTypography variant="caption" fontWeight="medium" fontSize="0.7rem">
            {payment.razorpay_payment_id}
          </MDTypography>
        ),
        status: getStatusChip(payment.status),
        createdAt: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatDate(payment.createdAt)}
          </MDTypography>
        ),
        action: (
          <Tooltip title="View Details">
            <IconButton
              color="info"
              onClick={() => handleViewDialog(payment)}
              size="small"
              sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      })),
    [filteredPayments]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3} sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "24px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <PaymentIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    Payment Management
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                  {/* <MDButton
                    variant="gradient"
                    color="dark"
                    startIcon={<FilterListIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{ borderRadius: "12px", textTransform: "none" }}
                  >
                    Filters
                  </MDButton> */}
                  <MDButton
                    variant="gradient"
                    color="dark"
                    startIcon={
                      exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />
                    }
                    onClick={exportToExcel}
                    disabled={exporting}
                    sx={{ borderRadius: "12px", textTransform: "none" }}
                  >
                    {exporting ? "Exporting..." : "Export to Excel"}
                  </MDButton>
                </MDBox>
              </MDBox>

              {/* Filters Section */}
              {!showFilters && (
                <Fade in={!showFilters}>
                  <Box sx={{ pt: 3, pl: 3 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Search by User"
                          value={filterUser}
                          onChange={(e) => setFilterUser(e.target.value)}
                          placeholder="Enter user name..."
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Search by Plan"
                          value={filterPlan}
                          onChange={(e) => setFilterPlan(e.target.value)}
                          placeholder="Enter plan name..."
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          fullWidth
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                          variant="outlined"
                          size="small"
                          SelectProps={{ native: true }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        >
                          <option value="">All Status</option>
                          <option value="success">Success</option>
                          <option value="created">Pending</option>
                          <option value="failed">Failed</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                          fullWidth
                          sx={{ borderRadius: "10px", height: "40px" }}
                        >
                          Clear
                        </MDButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Fade>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : filteredPayments.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No payments found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* VIEW PAYMENT DETAILS DIALOG */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${metaBlue}, ${metaRed}, ${metaBlue})`,
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => setViewDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(33, 150, 243, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle sx={{ pb: 1, pt: 3, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Zoom in={viewDialogOpen} timeout={600}>
            <MDBox display="flex" flexDirection="column" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  background: `linear-gradient(135deg, ${metaBlue} 0%, ${metaRed} 100%)`,
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 20px rgba(33, 150, 243, 0.3)",
                  animation: `${float} 2s ease-in-out infinite`,
                }}
              >
                <ReceiptIcon sx={{ fontSize: 45, color: "#fff" }} />
              </MDBox>
              <MDTypography variant="h4" fontWeight="bold">
                Payment Details
              </MDTypography>
              {selectedPayment && getStatusChip(selectedPayment.status)}
            </MDBox>
          </Zoom>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={viewDialogOpen} timeout={800}>
            <Box sx={{ py: 2 }}>
              <Grid container spacing={3}>
                {/* User Information */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: "20px", bgcolor: "#e3f2fd", height: "100%" }}>
                    <MDTypography variant="h6" fontWeight="bold" mb={2} color="primary">
                      <PersonIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                      User Information
                    </MDTypography>
                    <Stack spacing={2}>
                      <MDBox display="flex" alignItems="center" gap={2}>
                        <Avatar
                          src={selectedPayment?.user?.profile_photo}
                          sx={{ width: 50, height: 50, border: `2px solid ${metaBlue}` }}
                        >
                          <PersonIcon />
                        </Avatar>
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="bold">
                            {selectedPayment?.user?.name || "N/A"}
                          </MDTypography>
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            {selectedPayment?.user?.email_id}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <Divider />
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <CreditCardIcon sx={{ color: metaBlue }} />
                        <MDTypography variant="button" fontWeight="medium">
                          User ID: {selectedPayment?.user?._id}
                        </MDTypography>
                      </MDBox>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Plan Information */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, borderRadius: "20px", bgcolor: "#fff3e0", height: "100%" }}>
                    <MDTypography variant="h6" fontWeight="bold" mb={2} color="warning">
                      <PaymentIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                      Plan Information
                    </MDTypography>
                    <Stack spacing={2}>
                      <MDBox>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Plan Name
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="bold">
                          {selectedPayment?.plan?.name || "N/A"}
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Amount
                        </MDTypography>
                        <MDTypography variant="h4" fontWeight="bold" color="error">
                          {formatAmount(selectedPayment?.amount)}
                        </MDTypography>
                      </MDBox>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Payment Details */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, borderRadius: "20px", bgcolor: "#f8f9fa" }}>
                    <MDTypography variant="h6" fontWeight="bold" mb={2}>
                      Payment Information
                    </MDTypography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Razorpay Order ID
                        </MDTypography>
                        <Paper sx={{ p: 1.5, mt: 0.5, bgcolor: "white", borderRadius: "10px" }}>
                          <MDTypography variant="button" fontWeight="medium">
                            {selectedPayment?.razorpay_order_id}
                          </MDTypography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Razorpay Payment ID
                        </MDTypography>
                        <Paper sx={{ p: 1.5, mt: 0.5, bgcolor: "white", borderRadius: "10px" }}>
                          <MDTypography variant="button" fontWeight="medium">
                            {selectedPayment?.razorpay_payment_id}
                          </MDTypography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Currency
                        </MDTypography>
                        <Paper sx={{ p: 1.5, mt: 0.5, bgcolor: "white", borderRadius: "10px" }}>
                          <MDTypography variant="button" fontWeight="medium">
                            {selectedPayment?.currency}
                          </MDTypography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Created At
                        </MDTypography>
                        <Paper sx={{ p: 1.5, mt: 0.5, bgcolor: "white", borderRadius: "10px" }}>
                          <MDTypography variant="button" fontWeight="medium">
                            {formatDate(selectedPayment?.createdAt)}
                          </MDTypography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Status Summary */}
                <Grid item xs={12}>
                  <Paper
                    sx={{ p: 3, borderRadius: "20px", textAlign: "center", bgcolor: "#f8f9fa" }}
                  >
                    <MDTypography variant="h6" fontWeight="bold" mb={2}>
                      Payment Status
                    </MDTypography>
                    <Box sx={{ display: "inline-block" }}>
                      {getStatusChip(selectedPayment?.status)}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => setViewDialogOpen(false)}
            sx={{ borderRadius: "12px", px: 4 }}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default PaymentList;
